import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

const CoursesCategoryRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/courses?category=${slug ?? ""}`} replace />;
};
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
// Index stays eager - it's the LCP route and most common entry point
import Index from "./pages/Index";
import CookieConsentBanner from "./components/CookieConsentBanner";
import ScrollToTop from "./components/ScrollToTop";
import SmoothScroll from "./components/SmoothScroll";
import { AuthProvider } from "./contexts/AuthContext";
import AssessmentMigrationRunner from "./components/auth/AssessmentMigrationRunner";

import AdminGuard from "./components/admin/AdminGuard";
import { captureFirstTouch, getAnonymousId } from "./lib/tracking";
import { startHeartbeat } from "./lib/heartbeat";

const TrackingBootstrap = () => {
  useEffect(() => {
    getAnonymousId();
    captureFirstTouch();
    startHeartbeat();
  }, []);
  return null;
};

// Lazy-loaded public pages - each ships its own JS chunk
const Assessment = lazy(() => import("./pages/Assessment"));
const Results = lazy(() => import("./pages/Results"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CareerMatches = lazy(() => import("./pages/CareerMatches"));
const CheckoutMatches = lazy(() => import("./pages/CheckoutMatches"));
const Blueprint = lazy(() => import("./pages/Blueprint"));
const MicroQuiz = lazy(() => import("./pages/MicroQuiz"));
const RefinedResults = lazy(() => import("./pages/RefinedResults"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));

const CareersIndex = lazy(() => import("./pages/CareersIndex"));
const CareerDetail = lazy(() => import("./pages/CareerDetail"));
const CareerIntentHub = lazy(() => import("./pages/CareerIntentHub"));
const SavedCareers = lazy(() => import("./pages/SavedCareers"));
const Account = lazy(() => import("./pages/Account"));
const EmailVerification = lazy(() => import("./pages/EmailVerification"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogTaxonomy = lazy(() => import("./pages/BlogTaxonomy"));
const Reviews = lazy(() => import("./pages/Reviews"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages - lazy-loaded so public visitors never download admin JS
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminPostList = lazy(() => import("./pages/admin/AdminPostList"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminCustomerList = lazy(() => import("./pages/admin/AdminCustomerList"));
const AdminWipeCustomers = lazy(() => import("./pages/admin/AdminWipeCustomers"));
const AdminTrackingDebug = lazy(() => import("./pages/admin/AdminTrackingDebug"));
const AdminLiveDashboard = lazy(() => import("./pages/admin/AdminLiveDashboard"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminLifecycle = lazy(() => import("./pages/admin/AdminLifecycle"));
const AdminCampaignList = lazy(() => import("./pages/admin/AdminCampaignList"));
const AdminCampaignEditor = lazy(() => import("./pages/admin/AdminCampaignEditor"));
const AdminCustomerDetail = lazy(() => import("./pages/admin/AdminCustomerDetail"));
const AdminOrderList = lazy(() => import("./pages/admin/AdminOrderList"));
const AdminOrderDetail = lazy(() => import("./pages/admin/AdminOrderDetail"));
const AdminPostEditor = lazy(() => import("./pages/admin/AdminPostEditor"));
const AdminClusters = lazy(() => import("./pages/admin/AdminClusters"));
const AdminContentSystem = lazy(() => import("./pages/admin/AdminContentSystem"));
const AdminAutomation = lazy(() => import("./pages/admin/AdminAutomation"));
const AdminTopicalMap = lazy(() => import("./pages/admin/AdminTopicalMap"));
const AdminContentPipeline = lazy(() => import("./pages/admin/AdminContentPipeline"));
const AdminBlogImagePreview = lazy(() => import("./pages/admin/AdminBlogImagePreview"));
const AdminBrokenLinks = lazy(() => import("./pages/admin/AdminBrokenLinks"));
const AdminSocialQueue = lazy(() => import("./pages/admin/AdminSocialQueue"));
const AdminSocialSettings = lazy(() => import("./pages/admin/AdminSocialSettings"));
const AdminCareerList = lazy(() => import("./pages/admin/AdminCareerList"));
const AdminCareerEditor = lazy(() => import("./pages/admin/AdminCareerEditor"));
const AdminCareersAll = lazy(() => import("./pages/admin/AdminCareersAll"));
const AdminIndexNow = lazy(() => import("./pages/admin/AdminIndexNow"));
const AdminReviewList = lazy(() => import("./pages/admin/AdminReviewList"));
const AdminReviewDetail = lazy(() => import("./pages/admin/AdminReviewDetail"));
const AdminEmailTemplates = lazy(() => import("./pages/admin/AdminEmailTemplates"));
const AdminMarketingForms = lazy(() => import("./pages/admin/AdminMarketingForms"));
const AdminMarketingSegments = lazy(() => import("./pages/admin/AdminMarketingSegments"));
const AdminMarketingFlows = lazy(() => import("./pages/admin/AdminMarketingFlows"));
const AdminMarketingCampaigns = lazy(() => import("./pages/admin/AdminMarketingCampaigns"));
const AdminMarketingCampaignEditor = lazy(() => import("./pages/admin/AdminMarketingCampaignEditor"));
const AdminMarketingFlowDetail = lazy(() => import("./pages/admin/AdminMarketingFlowDetail"));
const AdminMarketingFormEditor = lazy(() => import("./pages/admin/AdminMarketingFormEditor"));
const AdminEmailTemplateEditor = lazy(() => import("./pages/admin/AdminEmailTemplateEditor"));
const AdminAffiliates = lazy(() => import("./pages/admin/AdminAffiliates"));
const AdminAffiliateCourses = lazy(() => import("./pages/admin/AdminAffiliateCourses"));
const AdminAffiliateProviders = lazy(() => import("./pages/admin/AdminAffiliateProviders"));
const AdminCourseReviewQueue = lazy(() => import("./pages/admin/AdminCourseReviewQueue"));
const AdminGrowthEngine = lazy(() => import("./pages/admin/AdminGrowthEngine"));
const AdminGrowthV2 = lazy(() => import("./pages/admin/AdminGrowthV2"));
const AdminCourseExceptions = lazy(() => import("./pages/admin/AdminCourseExceptions"));
const AdminCourseConversionsImport = lazy(() => import("./pages/admin/AdminCourseConversionsImport"));
const AdminCoursesCatalogue = lazy(() => import("./pages/admin/AdminCoursesCatalogue"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));
const CourseTopic = lazy(() => import("./pages/CourseTopic"));
const Courses = lazy(() => import("./pages/Courses"));

const queryClient = new QueryClient();

// Minimal fallback - intentionally blank so it never flashes content during fast chunk loads
const RouteFallback = () => <div className="min-h-screen bg-background" aria-hidden="true" />;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <TrackingBootstrap />
        <AssessmentMigrationRunner>
        <BrowserRouter>
          <SmoothScroll>
            <ScrollToTop />
            <CookieConsentBanner />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/assessment" element={<Assessment />} />
              <Route path="/results" element={<Results />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/career-matches" element={<CareerMatches />} />
              <Route path="/checkout/matches" element={<CheckoutMatches />} />
              <Route path="/blueprint" element={<Blueprint />} />
              <Route path="/blueprint/:careerSlug" element={<Blueprint />} />
              <Route path="/accelerator" element={<Navigate to="/blueprint" replace />} />
              <Route path="/micro-quiz/:careerId" element={<MicroQuiz />} />
              <Route path="/refined-results/:careerId" element={<RefinedResults />} />
              <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
              <Route path="/admin/customers" element={<AdminGuard><AdminCustomerList /></AdminGuard>} />
              <Route path="/admin/customers/:id" element={<AdminGuard><AdminCustomerDetail /></AdminGuard>} />
              <Route path="/admin/wipe" element={<AdminGuard><AdminWipeCustomers /></AdminGuard>} />
              <Route path="/admin/tracking-debug" element={<AdminGuard><AdminTrackingDebug /></AdminGuard>} />
              <Route path="/admin/live" element={<AdminGuard><AdminLiveDashboard /></AdminGuard>} />
              <Route path="/admin/analytics" element={<AdminGuard><AdminAnalytics /></AdminGuard>} />
              <Route path="/admin/lifecycle" element={<AdminGuard><AdminLifecycle /></AdminGuard>} />
              <Route path="/admin/campaigns" element={<AdminGuard><AdminCampaignList /></AdminGuard>} />
              <Route path="/admin/campaigns/:id" element={<AdminGuard><AdminCampaignEditor /></AdminGuard>} />
              <Route path="/admin/orders" element={<AdminGuard><AdminOrderList /></AdminGuard>} />
              <Route path="/admin/orders/:id" element={<AdminGuard><AdminOrderDetail /></AdminGuard>} />
              <Route path="/admin/settings" element={<AdminGuard><AdminSettings /></AdminGuard>} />
              <Route path="/admin/blog" element={<AdminGuard><AdminPostList /></AdminGuard>} />
              <Route path="/admin/blog/clusters" element={<AdminGuard><AdminClusters /></AdminGuard>} />
              <Route path="/admin/blog/content-system" element={<AdminGuard><AdminContentSystem /></AdminGuard>} />
              <Route path="/admin/blog/ai" element={<Navigate to="/admin/blog/pipeline" replace />} />
              <Route path="/admin/blog/image-preview" element={<AdminGuard><AdminBlogImagePreview /></AdminGuard>} />
              <Route path="/admin/blog/automation" element={<AdminGuard><AdminAutomation /></AdminGuard>} />
             <Route path="/admin/blog/topical-map" element={<AdminGuard><AdminTopicalMap /></AdminGuard>} />
             <Route path="/admin/blog/pipeline" element={<AdminGuard><AdminContentPipeline /></AdminGuard>} />
              <Route path="/admin/blog/broken-links" element={<AdminGuard><AdminBrokenLinks /></AdminGuard>} />
              <Route path="/admin/blog/social-queue" element={<AdminGuard><AdminSocialQueue /></AdminGuard>} />
              <Route path="/admin/blog/social-settings" element={<AdminGuard><AdminSocialSettings /></AdminGuard>} />
              <Route path="/admin/blog/:id/edit" element={<AdminGuard><AdminPostEditor /></AdminGuard>} />
              <Route path="/admin/careers" element={<AdminGuard><AdminCareersAll /></AdminGuard>} />
              <Route path="/admin/careers/seo" element={<AdminGuard><AdminCareerList /></AdminGuard>} />
              <Route path="/admin/careers/:slug" element={<AdminGuard><AdminCareerEditor /></AdminGuard>} />
              <Route path="/admin/affiliates" element={<AdminGuard><AdminAffiliates /></AdminGuard>} />
              <Route path="/admin/affiliate-courses" element={<AdminGuard><AdminAffiliateCourses /></AdminGuard>} />
              <Route path="/admin/affiliate-providers" element={<AdminGuard><AdminAffiliateProviders /></AdminGuard>} />
              <Route path="/admin/growth-engine" element={<AdminGrowthEngine />} />
              <Route path="/admin/courses/growth" element={<AdminGrowthV2 />} />
              <Route path="/admin/courses/review-queue" element={<AdminCourseReviewQueue />} />
              <Route path="/admin/courses/exceptions" element={<AdminCourseExceptions />} />
              <Route path="/admin/courses/conversions-import" element={<AdminCourseConversionsImport />} />
              <Route path="/admin/courses/catalogue" element={<AdminGuard><AdminCoursesCatalogue /></AdminGuard>} />
              <Route path="/admin/indexnow" element={<AdminGuard><AdminIndexNow /></AdminGuard>} />
              <Route path="/admin/reviews" element={<AdminGuard><AdminReviewList /></AdminGuard>} />
              <Route path="/admin/reviews/:id" element={<AdminGuard><AdminReviewDetail /></AdminGuard>} />
              <Route path="/admin/email-templates" element={<AdminGuard><AdminEmailTemplates /></AdminGuard>} />
              <Route path="/admin/emails/templates" element={<AdminGuard><AdminEmailTemplates /></AdminGuard>} />
              <Route path="/admin/email/templates/:slug" element={<AdminGuard><AdminEmailTemplateEditor /></AdminGuard>} />
              <Route path="/admin/email-templates/:slug" element={<AdminGuard><AdminEmailTemplateEditor /></AdminGuard>} />
              <Route path="/admin/marketing/forms" element={<AdminGuard><AdminMarketingForms /></AdminGuard>} />
              <Route path="/admin/marketing/forms/:id" element={<AdminGuard><AdminMarketingFormEditor /></AdminGuard>} />
              <Route path="/admin/marketing/segments" element={<AdminGuard><AdminMarketingSegments /></AdminGuard>} />
              <Route path="/admin/marketing/flows" element={<AdminGuard><AdminMarketingFlows /></AdminGuard>} />
              <Route path="/admin/marketing/flows/:id" element={<AdminGuard><AdminMarketingFlowDetail /></AdminGuard>} />
              <Route path="/admin/marketing/campaigns" element={<AdminGuard><AdminMarketingCampaigns /></AdminGuard>} />
              <Route path="/admin/marketing/campaigns/new" element={<AdminGuard><AdminMarketingCampaignEditor /></AdminGuard>} />
              <Route path="/admin/marketing/campaigns/:id" element={<AdminGuard><AdminMarketingCampaignEditor /></AdminGuard>} />
              <Route path="/email-preferences" element={<EmailPreferences />} />
              <Route path="/verify" element={<EmailVerification />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/about" element={<HowItWorks />} />
              <Route path="/explore" element={<Navigate to="/careers" replace />} />
              <Route path="/careers" element={<CareersIndex />} />
              <Route path="/careers/page" element={<Navigate to="/careers" replace />} />
              <Route path="/careers/page/1" element={<Navigate to="/careers" replace />} />
              <Route path="/careers/page/:page" element={<CareersIndex />} />
              <Route path="/careers/intent/:tag" element={<CareerIntentHub />} />
              <Route path="/careers/:slug" element={<CareerDetail />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/page" element={<Navigate to="/courses" replace />} />
              <Route path="/courses/page/1" element={<Navigate to="/courses" replace />} />
              <Route path="/courses/page/:page" element={<Courses />} />
              <Route path="/courses/category/:slug" element={<CoursesCategoryRedirect />} />
              <Route path="/courses/:slug" element={<CourseTopic />} />

              <Route path="/saved" element={<SavedCareers />} />
              <Route path="/my-path" element={<Navigate to="/results" replace />} />
              <Route path="/account" element={<Account />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/category/:slug" element={<BlogTaxonomy mode="category" />} />
              <Route path="/blog/tag/:slug" element={<BlogTaxonomy mode="tag" />} />
              <Route path="/blog/author/:slug" element={<BlogTaxonomy mode="author" />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </SmoothScroll>
        </BrowserRouter>
        </AssessmentMigrationRunner>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
